import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCompanyDashboard } from '../lib/dashboard'
import { openShareLink } from '../lib/shareLinkQueries'
import { CalendarSection } from '../pages/calendar/CalendarPage'
import { cardClass, emptyStateClass, Notice } from './ui'

/** Opens a `/share/<token>` link, then reloads into the public view; the token leaves the address bar first. */
export function ShareOpen({ token }: { token: string }) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    window.history.replaceState(null, '', '/')
    openShareLink(token)
      .then(() => window.location.replace('/'))
      .catch(() => setFailed(true))
  }, [token])

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-50 p-4 dark:bg-slate-900">
      {failed ? (
        <Notice tone="danger" className="max-w-md">
          {t('publicview.openFailed')}
        </Notice>
      ) : (
        <p role="status" className="max-w-md text-center text-sm text-slate-600 dark:text-slate-300">
          {t('publicview.opening')}
        </p>
      )}
    </main>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className={cardClass}>
      <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1 text-3xl font-semibold tabular-nums text-slate-800 dark:text-slate-100">{value}</p>
    </div>
  )
}

/** The share-link viewer's dashboard: company figures and the company calendar, nothing personal. */
export function PublicDashboard() {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useCompanyDashboard()
  if (isLoading) return <p className={emptyStateClass}>{t('action.loading')}</p>
  if (isError || !data) return <Notice tone="danger">{t('publicview.dashboardError')}</Notice>
  const teams = (data.headcount?.by_team ?? []).filter((b) => b.value > 0)
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label={t('publicview.headcount')} value={data.headcount?.active ?? 0} />
        <Stat label={t('publicview.desksFree')} value={data.desks?.free_today ?? 0} />
        <Stat label={t('publicview.desksBooked')} value={data.desks?.booked_today ?? 0} />
      </div>
      {teams.length > 0 && (
        <section className={cardClass}>
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">{t('publicview.teams')}</h2>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {teams.map((bucket) => (
              <li key={bucket.label} className="flex justify-between gap-2 text-sm">
                <span className="truncate text-slate-700 dark:text-slate-200">{bucket.label}</span>
                <span className="tabular-nums text-slate-500 dark:text-slate-400">{bucket.value}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className={cardClass}>
        <CalendarSection companyOnly />
      </section>
    </div>
  )
}
